import { Injectable, inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ErrorHandlerService } from '../shared/services/error-handler.service';
import { ValidationErrorHandlerService } from '../shared/services/validation-error-handler.service';

/**
 * Interceptor obsługujący błędy HTTP
 * 
 * - Transformuje błędy do ustandaryzowanego formatu
 * - AUTOMATYCZNIE pokazuje toasty dla błędów walidacji
 * - Pozostałe błędy przekazuje do ErrorHandlerService (toast)
 * 
 * Field errors są obsługiwane przez @Input backendErrors w FormErrorMsgComponent
 */
@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {

  constructor(
    private _errorHandlerService: ErrorHandlerService,
    private _validationHandler: ValidationErrorHandlerService
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          // Pomiń błędy ładowania translacji
          if (request.url.includes('/assets/i18n/')) {
            return throwError(() => error);
          }

          // 401 Unauthorized - obsługiwane przez TokenRefreshInterceptor
          // NIE pokazuj toasta - token refresh może przywrócić sesję
          if (error.status === 401) {
            return throwError(() => error);
          }

          // Przekształć błąd do ustandaryzowanego formatu
          const transformedError = this.transformError(error);

          // Sprawdź czy to błąd walidacji
          const isValidationError = this.isValidationError(transformedError);

          if (isValidationError) {
            // Pokaż TOAST (FIELD ERRORS obsługuje komponent przez backendErrors @Input)
            this._validationHandler.handleValidationError(transformedError);
          } else {
            // Inne błędy - obsługuje ErrorHandlerService
            this._errorHandlerService.catchErrorBackend(transformedError);
          }

          return throwError(() => transformedError);
        })
      );
  }
  
  /**
   * Sprawdź czy to błąd walidacji (pomijany w globalnym handlerze)
   */
  private isValidationError(error: any): boolean {
    return error.errorCode === 'VALIDATION.MULTIPLE_ERRORS' 
      && !!error.validationErrors;
  }
  
  /**
   * Przekształć HttpErrorResponse na ustandaryzowany format
   * Wyciąga dane z error.error (response body) na główny poziom
   */
  private transformError(error: HttpErrorResponse): any {
    const body = error.error;
    
    return {
      status: error.status,
      statusText: error.statusText,
      url: error.url,
      // Wyciągnij dane z body
      errorCode: body?.errorCode,
      message: body?.message,
      validationErrors: body?.validationErrors,
      data: body?.data,
      errors: body?.errors,
      // Backward compatibility dla starych błędów
      tempToken: body?.tempToken,
      userId: body?.userId,
      mfaMethod: body?.mfaMethod
    };
  }
}
